import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Key,
  Link as LinkIcon,
  Building2,
  Copy,
  Check,
  Eye,
  EyeOff,
  RefreshCw,
  Save,
  AlertTriangle,
  Percent,
  Info,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useQuery } from "@tanstack/react-query";
import * as merchantService from "@/shared/api/services/merchantService";

export default function MerchantSettings() {
  const { t } = useLanguage();

  const { data: merchantProfile } = useQuery({
    queryKey: ["merchant-profile"],
    queryFn: () => merchantService.getProfile(),
  });
  const [apiKey] = useState("pk_live_RG001xK9mN3pL2qR5tY8wZ4aB7cD0eF");
  const [secretKey] = useState("sk_live_RG001aB3cD4eF5gH6iJ7kL8mN9oP0qRsT1uV2wX");
  const [showSecret, setShowSecret] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [callbackUrl, setCallbackUrl] = useState("https://royalgaming.com/api/payment/callback");
  const [webhookUrl, setWebhookUrl] = useState("https://royalgaming.com/api/payment/webhook");
  const [saving, setSaving] = useState(false);

  const [profile, setProfile] = useState({
    businessName: "Royal Gaming Co., Ltd.",
    email: "finance@royalgaming.com",
    supportContact: "+66 2 123 4567",
  });

  const copyToClipboard = (text, keyType) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyType);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const maskKey = (key) => {
    return key.slice(0, 12) + "••••••••••••••••" + key.slice(-4);
  };

  const handleSaveUrls = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 1000));
    setSaving(false);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 1000));
    setSaving(false);
  };

  return (
    <MerchantLayout currentPage="MerchantSettings" merchantName="Royal Gaming" merchantCode="MRC001">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("settings")}</h1>
          <p className="text-slate-500">{t("manageProfile")}</p>
        </div>

        {/* API Keys */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Key className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle>{t("apiKeys")}</CardTitle>
                  <CardDescription>{t("yourAuthCredentials")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* API Key */}
              <div className="space-y-2">
                <Label>{t("apiKeyPublic")}</Label>
                <div className="flex gap-2">
                  <div className="flex-1 bg-slate-50 border rounded-lg px-4 py-2.5">
                    <code className="text-sm font-mono">{apiKey}</code>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(apiKey, "api")}
                  >
                    {copiedKey === "api" ? (
                      <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Secret Key */}
              <div className="space-y-2">
                <Label>{t("secretKey")}</Label>
                <div className="flex gap-2">
                  <div className="flex-1 bg-slate-50 border rounded-lg px-4 py-2.5 flex items-center justify-between">
                    <code className="text-sm font-mono">
                      {showSecret ? secretKey : maskKey(secretKey)}
                    </code>
                    <button
                      onClick={() => setShowSecret(!showSecret)}
                      className="text-slate-400 hover:text-slate-600 ml-2"
                    >
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(secretKey, "secret")}
                  >
                    {copiedKey === "secret" ? (
                      <Check className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Regenerate */}
              <div className="pt-4 border-t flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">{t("regenerateKeys")}</p>
                  <p className="text-xs text-slate-500">{t("invalidateCurrentKeys")}</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="gap-2 text-amber-600 hover:text-amber-700">
                      <RefreshCw className="h-4 w-4" />
                      {t("regenerate")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        {t("regenerateApiKeys")}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("invalidateImmediately")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                      <AlertDialogAction className="bg-amber-600 hover:bg-amber-700">
                        {t("regenerateKeys")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Callback URLs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                  <LinkIcon className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle>{t("callbackUrls")}</CardTitle>
                  <CardDescription>{t("whereWeSendNotifications")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="callback">{t("callbackUrl")}</Label>
                <Input
                  id="callback"
                  value={callbackUrl}
                  onChange={(e) => setCallbackUrl(e.target.value)}
                  placeholder="https://your-site.com/api/callback"
                />
                <p className="text-xs text-slate-500">
                  {t("wePostToEndpoint")}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="webhook">{t("webhookUrl")}</Label>
                <Input
                  id="webhook"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://your-site.com/api/webhook"
                />
                <p className="text-xs text-slate-500">
                  {t("additionalEventNotifications")}
                </p>
              </div>

              <Button onClick={handleSaveUrls} disabled={saving} className="gap-2">
                {saving ? (
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {t("saveUrls")}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Fees & Commission Card */}
        {merchantProfile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Percent className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle>{t("feesAndCommission")}</CardTitle>
                    <CardDescription>{t("commissionRatesAndFeeInfo")}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Parent Commission Info */}
                {merchantProfile.parentName && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex gap-3">
                      <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-900 mb-2">
                          {t("givenBy")} {merchantProfile.parentType}: {merchantProfile.parentName}
                        </h4>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-blue-700 font-medium">{t("depositCommission")}</p>
                            <p className="text-blue-900 text-lg font-semibold">
                              {merchantProfile.parentDepositCommission !== null && merchantProfile.parentDepositCommission !== undefined
                                ? `${merchantProfile.parentDepositCommission}%`
                                : t("nA")}
                            </p>
                          </div>
                          <div>
                            <p className="text-blue-700 font-medium">{t("withdrawalCommission")}</p>
                            <p className="text-blue-900 text-lg font-semibold">
                              {merchantProfile.parentWithdrawalCommission !== null && merchantProfile.parentWithdrawalCommission !== undefined
                                ? `${merchantProfile.parentWithdrawalCommission}%`
                                : t("nA")}
                            </p>
                          </div>
                          <div>
                            <p className="text-blue-700 font-medium">{t("percentageFeeLabel")}</p>
                            <p className="text-blue-900 text-lg font-semibold">
                              {merchantProfile.parentPercentageFee !== null && merchantProfile.parentPercentageFee !== undefined
                                ? `${merchantProfile.parentPercentageFee}%`
                                : t("nA")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Commission Rates (Read-only) */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">{t("yourCommissionRates")}</Label>
                    <p className="text-xs text-slate-500 mt-1">{t("ratesAssignedToYou")}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">{t("depositCommission")} (%)</Label>
                      <Input
                        value={merchantProfile.depositCommission !== null && merchantProfile.depositCommission !== undefined 
                          ? merchantProfile.depositCommission 
                          : t("nA")}
                        disabled
                        className="bg-slate-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">{t("withdrawalCommission")} (%)</Label>
                      <Input
                        value={merchantProfile.withdrawalCommission !== null && merchantProfile.withdrawalCommission !== undefined 
                          ? merchantProfile.withdrawalCommission 
                          : t("nA")}
                        disabled
                        className="bg-slate-50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">{t("percentageFeeLabel")}</Label>
                      <Input
                        value={merchantProfile.percentageFee !== null && merchantProfile.percentageFee !== undefined 
                          ? merchantProfile.percentageFee 
                          : t("nA")}
                        disabled
                        className="bg-slate-50"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Business Profile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <CardTitle>{t("merchantProfile")}</CardTitle>
                  <CardDescription>{t("yourBusinessInfo")}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="businessName">{t("businessName")}</Label>
                <Input
                  id="businessName"
                  value={profile.businessName}
                  onChange={(e) => setProfile({ ...profile, businessName: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t("emailAddress")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="support">{t("supportContact")}</Label>
                <Input
                  id="support"
                  value={profile.supportContact}
                  onChange={(e) => setProfile({ ...profile, supportContact: e.target.value })}
                  placeholder="+66 XXX XXX XXXX"
                />
              </div>

              <Button onClick={handleSaveProfile} disabled={saving} className="gap-2">
                {saving ? (
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {t("saveProfile")}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </MerchantLayout>
  );
}